/**
 * Rivo - Claim points
 * Lets a logged-in customer claim points for a Rivo custom action
 * (e.g. an in-store event reached through a QR code).
 *
 * States (set on data-state): loading | login | claim | success | claimed | error
 *
 * Text placeholders: every element with data-rivo-template is re-rendered from that
 * template, replacing [points], [balance] and [first_name] with live values.
 */
if (!customElements.get('rivo-claim-points')) {
  class RivoClaimPoints extends HTMLElement {
    static RIVO_JS_WAIT = 5000;

    abortController = undefined;
    fallbackTimer = null;
    isSettled = false;
    isClaiming = false;
    placeholderValues = {};

    constructor() {
      super();
    }

    connectedCallback() {
      this.updatePlaceholders({
        points: this.rulePoints ?? this.dataset.pointsFallback,
        balance: '',
        first_name: this.dataset.firstName || '',
      });

      if (!this.dataset.customerId) {
        this.setState('login');
        return;
      }

      this.claimButton?.addEventListener('click', this.claim);
      this.retryButton?.addEventListener('click', this.loadStatus);
      this.loadStatus();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.fallbackTimer);
      document.removeEventListener('rivo-js-loaded', this.onRivoJSLoaded);
      this.claimButton?.removeEventListener('click', this.claim);
      this.retryButton?.removeEventListener('click', this.loadStatus);
    }

    get claimButton() {
      return this.querySelector('[data-rivo-claim]');
    }

    get claimLabel() {
      return this.querySelector('[data-rivo-claim-label]');
    }

    get retryButton() {
      return this.querySelector('[data-rivo-retry]');
    }

    /**
     * Points amount of the matching Rivo earning rule, when Rivo's config is on the page.
     * @returns {number|undefined}
     */
    get rulePoints() {
      const rules = window.Rivo?.loy_config?.ways_to_earn || [];
      return rules.find((rule) => rule.name === this.dataset.actionName)?.points_amount;
    }

    /**
     * Rivo's live app proxy path, falling back to the value set in Liquid.
     * @returns {string}
     */
    get proxyPath() {
      return window.Rivo?.global_config?.proxy_paths?.loy || this.dataset.proxyFallback;
    }

    get customerUrl() {
      return `${this.proxyPath}/loy/customers/${this.dataset.customerId}`;
    }

    setState(state) {
      this.dataset.state = state;
    }

    /**
     * @param {string} template - merchant text containing [points], [balance] or [first_name]
     * @returns {string}
     */
    fillPlaceholders(template = '') {
      return template.replace(/\[(points|balance|first_name)\]/g, (match, key) => this.placeholderValues[key] ?? '');
    }

    /**
     * @param {{points?: (number|string), balance?: (number|string), first_name?: string}} values
     */
    updatePlaceholders(values) {
      Object.assign(this.placeholderValues, values);

      for (const element of this.querySelectorAll('[data-rivo-template]')) {
        if (element === this.claimLabel && this.isClaiming) continue;

        // Only touch changed text: rewriting nodes inside the aria-live region re-announces them.
        const text = this.fillPlaceholders(element.dataset.rivoTemplate);
        if (element.textContent !== text) element.textContent = text;
      }

      const balance = this.querySelector('[data-rivo-balance]');
      if (balance) {
        const template = balance.dataset.rivoTemplate.trim();
        balance.hidden = !template || (template.includes('[balance]') && this.placeholderValues.balance === '');
      }
    }

    /**
     * Has this customer already claimed?
     * Primary: RivoJS.getCustomerDetails() (waits for rivo-js-loaded).
     * Fallback: the status endpoint, in case the Rivo app embed is not on this page.
     */
    loadStatus = () => {
      // No Rivo action configured: never send a claim that could award another event's points.
      if (!this.dataset.actionName) {
        this.setState('error');
        return;
      }

      this.setState('loading');
      this.isSettled = false;
      clearTimeout(this.fallbackTimer);

      if (this.loadViaRivoJS()) return;

      document.addEventListener('rivo-js-loaded', this.onRivoJSLoaded);
      this.fallbackTimer = setTimeout(this.onRivoJSTimeout, RivoClaimPoints.RIVO_JS_WAIT);
    };

    onRivoJSLoaded = () => {
      document.removeEventListener('rivo-js-loaded', this.onRivoJSLoaded);
      this.loadViaRivoJS();
    };

    onRivoJSTimeout = () => {
      if (this.isSettled) return;

      document.removeEventListener('rivo-js-loaded', this.onRivoJSLoaded);
      if (this.loadViaRivoJS()) return;

      this.isSettled = true;
      this.fetchStatus();
    };

    /**
     * @returns {boolean} true when RivoJS is available and the lookup has started
     */
    loadViaRivoJS() {
      if (this.isSettled || !window.RivoJS?.getCustomerDetails) return false;

      this.isSettled = true;
      this.fetchRivoJSDetails();
      return true;
    }

    async fetchRivoJSDetails() {
      try {
        this.applyDetails(await window.RivoJS.getCustomerDetails());
      } catch (error) {
        this.setState('claim');
      }
    }

    async fetchStatus() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(`${this.customerUrl}/status`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: this.abortController.signal,
        });

        if (response.status === 404) {
          this.setState('login');
          return;
        }

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        this.applyDetails(await this.parseJson(response));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setState('claim');
      }
    }

    /**
     * @param {Object} response - RivoJS customer details or the status endpoint payload
     */
    applyDetails(response) {
      const customer = response?.customer || response || {};
      const completedActions = customer.completed_custom_action_names || [];
      this.updatePlaceholders({
        points: this.rulePoints ?? this.placeholderValues.points,
        balance: customer.points_tally ?? customer.points ?? this.placeholderValues.balance,
      });
      this.setState(completedActions.includes(this.dataset.actionName) ? 'claimed' : 'claim');
    }

    claim = async () => {
      if (this.isClaiming || !this.dataset.actionName) return;

      this.setClaiming(true);
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(`${this.customerUrl}/record_custom_action`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ custom_action_name: this.dataset.actionName }),
          signal: this.abortController.signal,
        });
        const data = await this.parseJson(response);

        if (response.status === 200 && data.success !== false) {
          this.showSuccess(data);
        } else if (response.status === 429) {
          this.setState('claimed');
        } else if (response.status === 404) {
          this.setState('login');
        } else {
          this.setState('error');
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.setState('error');
      } finally {
        this.setClaiming(false);
      }
    };

    /**
     * @param {boolean} isClaiming
     */
    setClaiming(isClaiming) {
      this.isClaiming = isClaiming;

      // aria-disabled (not disabled) keeps keyboard focus on the button while it is unavailable.
      if (isClaiming) {
        this.claimButton?.setAttribute('aria-busy', 'true');
        this.claimButton?.setAttribute('aria-disabled', 'true');
      } else {
        this.claimButton?.removeAttribute('aria-busy');
        this.claimButton?.removeAttribute('aria-disabled');
      }

      if (this.claimLabel) {
        this.claimLabel.textContent = this.fillPlaceholders(
          isClaiming ? this.dataset.busyLabel : this.claimLabel.dataset.rivoTemplate
        );
      }
    }

    /**
     * @param {Response} response
     * @returns {Promise<Object>} parsed body, or an empty object when it is not JSON
     */
    async parseJson(response) {
      try {
        return await response.json();
      } catch (error) {
        return {};
      }
    }

    /**
     * @param {Object} data - record_custom_action response
     */
    showSuccess(data) {
      const pointsLog = data.points_log || {};
      this.updatePlaceholders({
        points: pointsLog.points_amount || pointsLog.amount || this.placeholderValues.points,
        balance: data.pretty_points_tally ?? data.points_tally ?? this.placeholderValues.balance,
      });

      // Lets other UI showing the balance (the header tier bar) update without a reload.
      const pointsTally = data.points_tally ?? data.pretty_points_tally;
      if (pointsTally !== undefined) {
        document.dispatchEvent(
          new CustomEvent('rivo:points-change', {
            detail: { pointsTally },
          })
        );
      }

      this.setState('success');
    }
  }

  customElements.define('rivo-claim-points', RivoClaimPoints);
}
